import { shiftFinancialTotals } from "../../helpers/shiftFinancialTotals";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { shiftActions } from "../../sagas/shiftSlice";
import { commercialActions } from "../../sagas/commercialSlice";
import { Badge, Button, Card, CardBody, Col, Container, FormGroup, Input, Label, Row, Spinner } from "reactstrap";
import { FaCalendarAlt, FaCashRegister, FaEye, FaFilter, FaMoneyBillWave, FaUndoAlt, FaUserClock } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { formatPrice } from "../../helpers/utility";
import { SHIFT_DETAIL_PAGE } from "../../routes/routeConstants";
import { useNavigate } from "react-router-dom";
import "./ShiftPage.css";

const periodOptions = [
  { value: null, label: "Tous" },
  { value: "today", label: "Aujourd'hui" },
  { value: "week", label: "Cette semaine" },
  { value: "month", label: "Ce mois" },
];

const SHIFT_FILTERS_STORAGE_KEY = "matjarpos.shiftPage.filters";
const defaultFilters = {
  commercialId: null,
  status: "OPENED",
  period: "month",
};

const pad = (n) => n.toString().padStart(2, "0");
const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const getPeriodRange = (period) => {
  if (!period) return { startDate: null, endDate: null };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (period === "today") {
    return { startDate: formatDate(today), endDate: formatDate(today) };
  }

  if (period === "week") {
    const day = today.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const start = new Date(today);
    start.setDate(today.getDate() + mondayOffset);
    return { startDate: formatDate(start), endDate: formatDate(today) };
  }

  if (period === "month") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { startDate: formatDate(start), endDate: formatDate(today) };
  }

  return { startDate: null, endDate: null };
};

const getInitialFilters = () => {
  if (typeof window === "undefined") return defaultFilters;

  try {
    const savedFilters = window.sessionStorage.getItem(SHIFT_FILTERS_STORAGE_KEY);
    return savedFilters ? { ...defaultFilters, ...JSON.parse(savedFilters) } : defaultFilters;
  } catch (error) {
    return defaultFilters;
  }
};

const getReturnsWithoutTicket = shift => shiftFinancialTotals(shift).noTicketReturns;
const getNetSalesAmount = shift => shiftFinancialTotals(shift).sales;
const getNetPaymentsAmount = shift => shiftFinancialTotals(shift).payments;

const ShiftPage = () => {
  const { t } = useTranslation();
  const { list: shifts = [], loading: shiftLoading } = useSelector((state) => state.shift);
  const { list: commercialList } = useSelector((state) => state.commercials);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [filters, setFilters] = useState(getInitialFilters);

  useEffect(() => {
    dispatch(commercialActions.list({ query: "" }));
  }, [dispatch]);

  useEffect(() => {
    const { period, ...restFilters } = filters;
    dispatch(shiftActions.fetchList({ ...restFilters, ...getPeriodRange(period) }));
  }, [dispatch, filters]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(SHIFT_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      // Storage can be unavailable in restricted browser contexts.
    }
  }, [filters]);

  const sortedShifts = useMemo(() => {
    return [...shifts].sort((a, b) => {
      const bTime = new Date(b.openingTime || b.createdAt || 0).getTime();
      const aTime = new Date(a.openingTime || a.createdAt || 0).getTime();
      return bTime - aTime;
    });
  }, [shifts]);

  const totals = useMemo(() => {
    return sortedShifts.reduce(
      (acc, shift) => {
        const returnsWithoutTicket = getReturnsWithoutTicket(shift);
        acc.salesAfterTicketReturns += getNetSalesAmount(shift);
        acc.returnsTicket += Number(shift.totalReturnsWithTicket || 0);
        acc.returnsNoTicket += returnsWithoutTicket;
        return acc;
      },
      { salesAfterTicketReturns: 0, returnsTicket: 0, returnsNoTicket: 0 }
    );
  }, [sortedShifts]);

  const commercialOptionsWithAll = [
    { value: null, label: t("common.all", "Tous") },
    ...(commercialList || []).map((com) => ({ value: com.id, label: com.fullName })),
  ];

  const clearFilters = () => {
    setFilters(defaultFilters);
  };

  const periodLabel = periodOptions.find((option) => option.value === filters.period)?.label;
  const subtitle = filters.status === "OPENED"
    ? `${sortedShifts.length} shifts ouverts${filters.period ? ` - ${periodLabel}` : ""}`
    : `${sortedShifts.length} shifts${filters.period ? ` - ${periodLabel}` : ""}`;

  const openDetails = (shift) => {
    navigate(SHIFT_DETAIL_PAGE, {
      state: {
        editMode: true,
        shift,
      },
    });
  };

  const statusBadge = (status) => {
    if (status === "OPENED") return <Badge color="warning" pill>{t("shifts.opened")}</Badge>;
    if (status === "CLOSED") return <Badge color="danger" pill>{t("shifts.closed")}</Badge>;
    if (status === "VALIDATED") return <Badge color="success" pill>{t("shifts.validated")}</Badge>;
    return <Badge color="secondary" pill>{status || "-"}</Badge>;
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString("fr-FR");
  };

  return (
    <Container fluid className="shift-page">
      <div className="shift-header">
        <div>
          <div className="shift-title">
            <FaUserClock />
            {t("shifts.title")}
          </div>
          <div className="shift-subtitle">{subtitle}</div>
        </div>
      </div>

      <Row className="g-3 mb-3">
        <Col lg={4} md={6}>
          <div className="shift-kpi">
            <FaCashRegister className="text-primary" />
            <span>Ventes nettes après retours</span>
            <strong>{formatPrice(totals.salesAfterTicketReturns)}</strong>
          </div>
        </Col>
        <Col lg={4} md={6}>
          <div className="shift-kpi">
            <FaUndoAlt className="text-warning" />
            <span>Retours avec ticket</span>
            <strong>{formatPrice(totals.returnsTicket)}</strong>
          </div>
        </Col>
        <Col lg={4} md={6}>
          <div className="shift-kpi">
            <FaUndoAlt className="text-danger" />
            <span>Retours sans ticket</span>
            <strong>{formatPrice(totals.returnsNoTicket)}</strong>
          </div>
        </Col>
      </Row>

      <Card className="shift-filter-card">
        <CardBody>
          <Row className="g-2 align-items-end">
            <Col xl={3} md={6}>
              <FormGroup className="mb-0">
                <Label className="shift-label">{t("shifts.caissier")}</Label>
                <Select
                  options={commercialOptionsWithAll}
                  value={commercialOptionsWithAll.find((option) => option.value === filters.commercialId)}
                  onChange={(selectedOption) => setFilters((f) => ({ ...f, commercialId: selectedOption ? selectedOption.value : null }))}
                  placeholder={t("shifts.select")}
                  isSearchable
                  isClearable
                  menuPortalTarget={typeof window !== "undefined" ? document.body : null}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </FormGroup>
            </Col>
            <Col xl={3} md={4} sm={6}>
              <FormGroup className="mb-0">
                <Label className="shift-label">{t("shifts.status")}</Label>
                <Input type="select" value={filters.status || ""} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || null }))}>
                  <option value="">{t("common.all", "Tous")}</option>
                  <option value="OPENED">{t("shifts.opened")}</option>
                  <option value="CLOSED">{t("shifts.closed")}</option>
                  <option value="VALIDATED">{t("shifts.validated")}</option>
                </Input>
              </FormGroup>
            </Col>
            <Col xl={4} md={8} sm={12}>
              <FormGroup className="mb-0">
                <Label className="shift-label">Période</Label>
                <div className="shift-period-filter">
                  {periodOptions.map((option) => (
                    <Button
                      key={option.value || "all"}
                      color={filters.period === option.value ? "primary" : "light"}
                      onClick={() => setFilters((f) => ({ ...f, period: option.value }))}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </FormGroup>
            </Col>
            <Col xl={1} md={2} sm={6}>
              <Button color="light" className="shift-reset-btn" onClick={clearFilters} title={t("common.clearFilters")}>
                <FaFilter />
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="shift-list-card">
        <CardBody className="p-0">
          {shiftLoading ? (
            <div className="shift-empty">
              <Spinner color="primary" />
              <div>{t("common.loading")}</div>
            </div>
          ) : sortedShifts.length === 0 ? (
            <div className="shift-empty">{t("shifts.noShifts")}</div>
          ) : (
            <div className="shift-list">
              {sortedShifts.map((shift) => {
                const netSalesAmount = getNetSalesAmount(shift);
                const netPaymentsAmount = getNetPaymentsAmount(shift);
                return (
                  <div className="shift-row" key={shift.id}>
                    <div className="shift-row-main">
                      <div className="shift-row-title">
                        <strong>{shift.shiftCode}</strong>
                        {statusBadge(shift.status)}
                      </div>
                      <div className="shift-row-meta">
                        <span><FaUserClock /> {shift.commercialName || "-"}</span>
                        <span><FaCalendarAlt /> {formatDateTime(shift.openingTime)}</span>
                        <span>{formatDateTime(shift.closingTime)}</span>
                      </div>
                    </div>

                    <div className="shift-row-amounts">
                      <div>
                        <span>Ventes nettes</span>
                        <strong>{formatPrice(netSalesAmount, false)} DH</strong>
                      </div>
                      <div>
                        <span>Retours ticket</span>
                        <strong>{formatPrice(shift.totalReturnsWithTicket || 0, false)} DH</strong>
                      </div>
                      <div>
                        <span>Retours sans ticket</span>
                        <strong>{formatPrice(getReturnsWithoutTicket(shift), false)} DH</strong>
                      </div>
                      <div>
                        <span><FaMoneyBillWave /> Encaissements nets</span>
                        <strong>{formatPrice(netPaymentsAmount, false)} DH</strong>
                      </div>
                    </div>

                    <Button color="primary" className="shift-action-btn" onClick={() => openDetails(shift)} title={t("shifts.consult")}>
                      <FaEye />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default ShiftPage;
